import { captureException } from '@sentry/react';
import { hoursToMilliseconds, startOfToday, subDays } from 'date-fns';
import { secondsToMilliseconds } from 'motion/react';
import { useCallback, useMemo } from 'react';
import { useEffectOnce, useInterval } from 'react-use';

import { PrintedOrdersRepository } from '../sqlite';

export interface UsePrintedOrdersDbValue {
  addOrder(orderId: string): Promise<void>;
  isOrderPrinted(orderId: string): Promise<boolean>;
}

export const usePrintedOrdersDb = () => {
  const repository = useMemo(() => new PrintedOrdersRepository(), []);

  const addOrder = useCallback(
    async (orderId: string) => {
      await repository.addOrder(orderId);
    },
    [repository],
  );

  const isOrderPrinted = useCallback(
    async (orderId: string) => {
      return await repository.isOrderPrinted(orderId);
    },
    [repository],
  );

  const cleanupOldOrders = useCallback(() => {
    const cutOffDate = subDays(startOfToday(), 3);

    console.log(
      `Cleaning up printed orders older than ${cutOffDate.toISOString()}`,
    );

    repository.cleanUpOldOrders(cutOffDate.getTime()).catch((error) => {
      console.error('Failed to clean up old printed orders:', error);

      captureException(error, {
        level: 'error',
        extra: { message: 'Failed to clean up old printed orders' },
      });
    });
  }, [repository]);

  useEffectOnce(() => {
    // Clean up orders on app-start. This makes sure that we always
    // do a cleanup regularly if the app if frequently closed and opened.
    // Timeout is used to allow the app to settle a bit before doing
    // the cleanup so we avoid impacting the app-start performance too much.
    const timeout = setTimeout(() => {
      cleanupOldOrders();
    }, secondsToMilliseconds(30));

    // Cleared so that StrictMode's double-invoked effect and unmounts do
    // not leave extra cleanups scheduled.
    return () => clearTimeout(timeout);
  });

  useInterval(() => {
    // If the app is always open we want to ensure we still do
    // regular cleanup of old printed orders.
    cleanupOldOrders();
  }, hoursToMilliseconds(8));

  return {
    addOrder,
    isOrderPrinted,
  };
};
